import React from "react";

import PropTypes from "prop-types";

import RbThemeProvider from "components/RbThemeProvider";
import useLocalStorage from "hooks/useLocalStorage";

import RbThemeConfigContext from "./RbThemeConfigContext";

const RbThemeConfigProvider = (props) => {
  const {
    primaryColor: primaryColorProp,
    secondaryColor: secondaryColorProp,
    dark: darkProp,
    contrast: contrastProp,
    fontSize: fontSizeProp,
    children,
  } = props;

  const [primaryColor, setPrimaryColor] = useLocalStorage(
    "primaryColor",
    primaryColorProp
  );
  const [secondaryColor, setSecondaryColor] = useLocalStorage(
    "secondaryColor",
    secondaryColorProp
  );
  const [dark, setDark] = useLocalStorage("dark", darkProp);
  const [contrast, setContrast] = useLocalStorage("contrast", contrastProp);
  const [fontSize, setFontSize] = useLocalStorage("fontSize", fontSizeProp);

  const handleChangePrimaryColor = React.useCallback(
    (value) => {
      setPrimaryColor(value);
    },
    [setPrimaryColor]
  );

  const handleChangeSecondaryColor = React.useCallback(
    (value) => {
      setSecondaryColor(value);
    },
    [setSecondaryColor]
  );

  const handleToggleDark = React.useCallback(() => {
    setDark((prevState) => !prevState);
  }, [setDark]);

  const handleChangeContrast = React.useCallback(
    (_, value) => {
      setContrast(value);
    },
    [setContrast]
  );

  const handleChangeFontSize = React.useCallback(
    (value) => {
      setFontSize(value);
    },
    [setFontSize]
  );

  const contextValue = React.useMemo(() => {
    return {
      primaryColor,
      secondaryColor,
      dark,
      contrast,
      fontSize,
      handleChangePrimaryColor,
      handleChangeSecondaryColor,
      handleToggleDark,
      handleChangeContrast,
      handleChangeFontSize,
    };
  }, [
    primaryColor,
    secondaryColor,
    dark,
    contrast,
    fontSize,
    handleChangePrimaryColor,
    handleChangeSecondaryColor,
    handleToggleDark,
    handleChangeContrast,
    handleChangeFontSize,
  ]);

  return (
    <RbThemeConfigContext.Provider value={contextValue}>
      <RbThemeProvider
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        dark={dark}
        contrast={contrast}
        fontSize={fontSize}
      >
        {children}
      </RbThemeProvider>
    </RbThemeConfigContext.Provider>
  );
};

RbThemeConfigProvider.propTypes = {
  dark: PropTypes.bool,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  children: PropTypes.node,
  contrast: PropTypes.number,
  fontSize: PropTypes.oneOf(["small", "medium", "large"]),
};

RbThemeConfigProvider.defaultProps = {
  dark: false,
  primaryColor: "#673AB7",
  secondaryColor: "#ff9800",
  children: null,
  contrast: 30,
  fontSize: "medium",
};

export default React.memo(RbThemeConfigProvider);
